import { motion, AnimatePresence } from 'framer-motion'
import { useEffect, useRef, useState } from 'react'
import PixelartIcon, { pixelartIcons } from './PixelartIcon'
import { useUsingTouch } from './utilsApp'
import { withInjectableUi } from './extendableSystem'
import styles from './Notification.module.css'

const duration = 0.2

// save pass: login

const toastHeight = 34
const PROGRESS_TICK_MS = 50

interface SmoothProgressState {
  total: number
  actual: number
  displayed: number
  rate: number
  sampleAt: number
}

const useSmoothedProgress = (current: number | undefined, total: number | undefined, enabled: boolean) => {
  const [displayed, setDisplayed] = useState(0)
  const motionRef = useRef<SmoothProgressState>({
    total: total ?? 0,
    actual: current ?? 0,
    displayed: 0,
    rate: 0,
    sampleAt: performance.now(),
  })

  useEffect(() => {
    if (!enabled || current === undefined || total === undefined || total <= 0) {
      setDisplayed(current ?? 0)
      return
    }

    const now = performance.now()
    const actual = Math.max(0, Math.min(total, current))
    const previous = motionRef.current
    if (previous.total !== total || actual < previous.actual) {
      const initialRate = Math.max(actual / 700, total / 12_000)
      motionRef.current = { total, actual, displayed: 0, rate: initialRate, sampleAt: now }
      setDisplayed(0)
      return
    }

    const elapsed = Math.max(1, now - previous.sampleAt)
    const sampleRate = Math.max(0, actual - previous.actual) / elapsed
    const blendedRate = sampleRate > 0
      ? previous.rate * 0.55 + sampleRate * 0.45
      : previous.rate
    const finishRate = actual >= total
      ? Math.max(0, total - previous.displayed) / 700
      : 0
    previous.actual = actual
    previous.rate = Math.max(blendedRate, finishRate, total / 20_000)
    previous.sampleAt = now
  }, [current, enabled, total])

  useEffect(() => {
    if (!enabled || total === undefined || total <= 0) return
    let lastTick = performance.now()
    const interval = setInterval(() => {
      const now = performance.now()
      const elapsed = Math.min(200, now - lastTick)
      lastTick = now
      const state = motionRef.current
      if (state.total <= 0) return

      const lead = state.rate * 700
      const target = state.actual >= state.total
        ? state.total
        : Math.min(state.total * 0.98, state.actual + lead)
      if (state.displayed >= target) return

      const catchUpRate = state.actual > state.displayed
        ? (state.actual - state.displayed) / 450
        : 0
      const next = Math.min(target, state.displayed + Math.max(state.rate, catchUpRate) * elapsed)
      state.displayed = next
      setDisplayed(next)
    }, PROGRESS_TICK_MS)
    return () => clearInterval(interval)
  }, [enabled, total])

  return displayed
}

interface NotificationProps {
  open: boolean
  message: string
  type?: 'message' | 'error' | 'progress'
  subMessage?: string
  icon?: string
  action?: () => void
  topPosition?: number

  currentProgress?: number
  totalProgress?: number
}

const NotificationBase = ({
  type = 'message',
  message,
  subMessage = '',
  open,
  icon = '',
  action = undefined as (() => void) | undefined,
  topPosition = 0,
  currentProgress,
  totalProgress,
}: NotificationProps) => {
  const isUsingTouch = useUsingTouch()
  const isError = type === 'error'
  icon ||= isError ? 'alert' : 'message'

  const isLoader = type === 'progress'
  const smoothProgress = useSmoothedProgress(currentProgress, totalProgress, isLoader)

  // Loader / progress toast — adopts the replay HUD "sharp glass" look and
  // docks into the left column, just under the info block (InfoViewPanel).
  if (isLoader) {
    const pct = currentProgress !== undefined && totalProgress !== undefined
      ? Math.min(100, (totalProgress ? smoothProgress / totalProgress : 0) * 100)
      : null
    const translatedMessage = translate(message)
    const loaderLabel = message === 'Загрузка чанков мира'
      ? 'загрузка чанков'
      : translatedMessage
    const loaderStat = currentProgress !== undefined && totalProgress !== undefined
      ? `${Math.round(currentProgress)} / ${Math.round(totalProgress)}`
      : (subMessage ? translate(subMessage) : '')
    const loaderIndex = Math.max(0, (topPosition || 1) - 1) // 0-based stack slot
    return <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0, y: 0 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: '-100%' }}
          transition={{ duration }}
          className={`app-notification ${styles.loaderToast}`}
          data-replay-ui
          data-holo-loader
          style={{ ['--loader-index' as any]: loaderIndex }}
          role="status"
          aria-live="polite"
        >
          {/* Multi-band scan noise — independent non-harmonic sweeps so the
              pattern never repeats during loading. See loaderBands in CSS. */}
          <div className={styles.loaderBands}>
            <span className={styles.loaderBand} />
            <span className={`${styles.loaderBand} ${styles.loaderBand2}`} />
            <span className={`${styles.loaderBand} ${styles.loaderBand3}`} />
          </div>
          <div className={styles.loaderBody}>
            <div className={styles.loaderHead}>
              <div className={styles.loaderTitle}>{loaderLabel}</div>
              {loaderStat && <div className={styles.loaderSub}>{loaderStat}</div>}
            </div>
            <div
              className={styles.loaderTrack}
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={pct ?? undefined}
              aria-label={translatedMessage}
            >
              {pct === null
                ? <div className={`${styles.loaderFill} ${styles.loaderFillIndeterminate}`} />
                : <div className={styles.loaderFill} style={{ width: `${pct}%` }} />}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  }

  const top = (topPosition * toastHeight) + (isUsingTouch ? 18 : 0) // mobile top buttons offset
  return <AnimatePresence>
    {open && (
      <motion.div
        initial={{ opacity: 0, y: 0 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: '-100%' }}
        transition={{ duration }}
        className={`app-notification ${isError ? 'error-notification' : ''}`}
        onClick={action}
        style={{
          position: 'fixed',
          top,
          right: 0,
          scale: 0.9,
          transformOrigin: 'right',
          whiteSpace: 'nowrap',
          fontSize: '9px',
          display: 'flex',
          gap: 4,
          alignItems: 'center',
          padding: '4px 5px',
          background: isError ? 'rgba(255, 0, 0, 0.7)' : 'rgba(0, 0, 0, 0.7)',
          borderRadius: top === 0 ? '0 0 0 5px' : '5px',
          pointerEvents: action ? 'auto' : 'none',
          zIndex: 1200,
        }}
      >
        <PixelartIcon
          iconName={icon}
          styles={{
            fontSize: 12,
            animation: 'none',
          }}
        />
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
        }}>
          <div style={{
            whiteSpace: 'normal',
          }}>
            {translate(message)}
          </div>
          <div style={{
            fontSize: '7px',
            whiteSpace: 'nowrap',
            color: 'lightgray',
            marginTop: 3,
          }}>
            {translate(subMessage)}
          </div>
        </div>
      </motion.div>
    )}
  </AnimatePresence>
}

export default withInjectableUi(NotificationBase, 'notification')
